/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
  type AxiosError,
} from 'axios'

import { authSessionStorageService } from '@/services/sessionStorageService'

const SESSION_TOKEN_KEY = 'X-Auth-Token'
const CSRF_TOKEN_KEY = 'X-CSRF-TOKEN'

/**
 * Safely retrieves a header value from the headers object.
 * It first checks for the exact key and then its lowercase version.
 *
 * @param headers - The headers object from the response.
 * @param key - The header key to retrieve.
 * @returns The header value as a string, or undefined.
 */
function getHeaderValue(
  headers: Record<string, any>,
  key: string,
): string | undefined {
  return headers[key] || headers[key.toLowerCase()]
}

/**
 * Updates session and CSRF tokens from response headers if they exist.
 *
 * @param headers - The response headers containing potential token updates.
 */
function updateTokensFromHeaders(headers: Record<string, any>): void {
  const authToken = getHeaderValue(headers, SESSION_TOKEN_KEY)
  const csrfToken = getHeaderValue(headers, CSRF_TOKEN_KEY)

  if (typeof authToken === 'string') {
    authSessionStorageService.setSession(authToken)
  }
  if (typeof csrfToken === 'string') {
    authSessionStorageService.setCsrfToken(csrfToken)
  }
}

/**
 * Handles HTTP 302 redirect responses by extracting tokens and redirect location.
 *
 * @param error - The Axios error containing the 302 response.
 * @returns A resolved promise with a custom response object.
 */
function handleRedirectResponse(error: AxiosError<unknown>): Promise<AxiosResponse> {
  const errorHeaders = error.response!.headers as Record<string, any>
  const redirectLocation = getHeaderValue(errorHeaders, 'X-Redirect-Location')
  
  console.debug('[Error Interceptor - 302]', {
    redirectLocation,
    authToken: getHeaderValue(errorHeaders, SESSION_TOKEN_KEY),
    csrfToken: getHeaderValue(errorHeaders, CSRF_TOKEN_KEY),
  })

  // Update tokens from the redirect response
  updateTokensFromHeaders(errorHeaders)

  // Construct a custom response object
  const customResponse: AxiosResponse = {
    data: redirectLocation,
    status: error.response!.status,
    statusText: error.response!.statusText,
    headers: error.response!.headers,
    config: error.config!, // assert that config exists
  }
  
  return Promise.resolve(customResponse)
}

/**
 * Handles unauthorized responses by clearing session and redirecting to welcome page.
 * BUT only if we're not on the login page (to allow login error handling).
 */
function handleUnauthorizedResponse(): void {
  // Check if we're currently on the login page
  const currentPath = window.location.pathname
  const isOnLoginPage = currentPath.includes('/login') || currentPath.includes('/auth/login')
  
  // If we're on the login page, don't redirect - let the login form handle the error
  if (isOnLoginPage) {
    console.debug('[Auth] Login attempt failed - letting login form handle error')
    return
  }
  
  // For all other pages, clear session and redirect to welcome
  console.debug('[Auth] Unauthorized access - redirecting to welcome page')
  authSessionStorageService.clearSession()
  window.location.href = '/welcome?fromLogout=true'
}

/**
 * Handles general error responses by updating tokens if present.
 *
 * @param errorHeaders - The error response headers.
 */
function handleGeneralErrorResponse(errorHeaders: Record<string, any>): void {
  console.debug('[Error Interceptor]', {
    authToken: getHeaderValue(errorHeaders, SESSION_TOKEN_KEY),
    csrfToken: getHeaderValue(errorHeaders, CSRF_TOKEN_KEY),
  })
  
  updateTokensFromHeaders(errorHeaders)
}

/**
 * Sets up Axios interceptors for:
 * - Attaching session and CSRF tokens to outgoing requests.
 * - Updating tokens based on response headers.
 * - Handling HTTP 302 redirects.
 *
 * @param axiosInstance - The Axios instance to configure.
 */
export function setupAxiosInterceptors(axiosInstance: AxiosInstance): void {
  // Request interceptor: attach tokens if available.
  axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
      // Ensure headers exists.
      config.headers = config.headers || {}

      const sessionId = authSessionStorageService.getSession()
      const csrfToken = authSessionStorageService.getCsrfToken()

      console.debug('[Request Interceptor]', { sessionId, csrfToken })

      if (sessionId) {
        config.headers[SESSION_TOKEN_KEY] = sessionId
      }
      if (csrfToken) {
        config.headers[CSRF_TOKEN_KEY] = csrfToken
      }
      return config
    },
    async (error: unknown) => Promise.reject(error),
  )

  // Response interceptor: update tokens from response headers.
  axiosInstance.interceptors.response.use(
    (response: AxiosResponse): AxiosResponse => {
      const headers = response.headers as Record<string, any>
      const authToken = getHeaderValue(headers, SESSION_TOKEN_KEY)
      const csrfToken = getHeaderValue(headers, CSRF_TOKEN_KEY)

      console.debug('[Response Interceptor]', { authToken, csrfToken })

      updateTokensFromHeaders(headers)
      
      return response
    },
    async (error: AxiosError<unknown>): Promise<AxiosResponse> => {
      if (!error.response) {
        return Promise.reject(error)
      }

      const status = error.response.status
      const errorHeaders = error.response.headers as Record<string, any>

      // Handle specific status codes
      if (status === 302) {
        return handleRedirectResponse(error)
      }
      
      if ([401, 403].includes(status)) {
        handleUnauthorizedResponse(error.config)
        return Promise.reject(error)
      }
      
      // Handle all other error cases
      handleGeneralErrorResponse(errorHeaders)
      return Promise.reject(error)
    },
  )
}